import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Zap,
  Plus,
  Play,
  Trash2,
  Power,
  PowerOff,
  Save,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { automationsApi } from "@/lib/crmApi";
import { STATUTS } from "@/lib/crmHelpers";

const TRIGGERS = [
  { value: "no_contact_days", label: "Sans contact depuis X jours" },
  { value: "status_age_days", label: "Âge depuis création > X jours" },
];

const ACTIONS = [
  { value: "create_rappel", label: "Créer un rappel" },
  { value: "set_priorite_haute", label: "Passer en priorité haute" },
];

const RuleRow = ({ rule, onToggle, onDelete, onRun, running }) => (
  <div
    className="rounded-lg border border-border/60 bg-card p-3"
    data-testid={`automation-row-${rule.id}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Zap
            className={`h-4 w-4 ${
              rule.enabled
                ? "text-[hsl(var(--accent-gold))]"
                : "text-muted-foreground"
            }`}
          />
          <span className="font-heading font-semibold">{rule.nom}</span>
          {!rule.enabled && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">
              Désactivée
            </span>
          )}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          Si{" "}
          <strong>
            {TRIGGERS.find((t) => t.value === rule.trigger)?.label}
          </strong>{" "}
          ≥ <strong>{rule.days_threshold}j</strong>
          {rule.statut_filter && (
            <>
              {" "}· Statut <strong>{rule.statut_filter}</strong>
            </>
          )}
          {rule.score_min !== null && rule.score_min !== undefined && (
            <>
              {" "}· Score ≥ <strong>{rule.score_min}</strong>
            </>
          )}
          {" "}→{" "}
          <strong>
            {ACTIONS.find((a) => a.value === rule.action)?.label}
          </strong>
        </div>
        {rule.action === "create_rappel" && (
          <div className="mt-1 text-[11px] italic text-muted-foreground">
            « {rule.rappel_message} »
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onRun(rule.id)}
          disabled={running}
          data-testid={`automation-run-${rule.id}`}
          title="Exécuter maintenant"
          className="h-7 gap-1 text-xs"
        >
          <Play className="h-3 w-3" />
          Lancer
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onToggle(rule)}
          data-testid={`automation-toggle-${rule.id}`}
          className="h-7 w-7 p-0"
          title={rule.enabled ? "Désactiver" : "Activer"}
        >
          {rule.enabled ? (
            <Power className="h-3.5 w-3.5 text-emerald-600" />
          ) : (
            <PowerOff className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDelete(rule.id, rule.nom)}
          data-testid={`automation-delete-${rule.id}`}
          className="h-7 w-7 p-0 text-muted-foreground hover:text-red-500"
          title="Supprimer"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  </div>
);

const NewRuleForm = ({ onCreated, onCancel, projetId = null }) => {
  const [form, setForm] = useState({
    nom: "",
    trigger: "no_contact_days",
    days_threshold: 3,
    statut_filter: "",
    score_min: "",
    action: "create_rappel",
    rappel_message: "Relance automatique : reprendre contact",
    enabled: true,
  });
  const [saving, setSaving] = useState(false);
  // Tracks an attempted submission so we can highlight the missing field
  // inline (toast alone was easy to miss — bug report #6).
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const nomMissing = !form.nom.trim();

  const submit = async () => {
    setSubmitAttempted(true);
    if (nomMissing) {
      toast.error("Nom de la règle obligatoire");
      return;
    }
    const days = Number(form.days_threshold);
    if (!days || days < 0) return toast.error("Seuil de jours invalide");
    setSaving(true);
    try {
      const payload = { ...form, days_threshold: days };
      if (payload.statut_filter === "" || payload.statut_filter === "all")
        payload.statut_filter = null;
      payload.score_min = payload.score_min === "" ? null : Number(payload.score_min);
      // Tag the rule to the active project so it only fires on that project's
      // leads (the backend runner scopes by projet_id).
      payload.projet_id = projetId || null;
      await automationsApi.create(payload);
      toast.success("Règle créée");
      onCreated();
    } catch (e) {
      toast.error("Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-dashed border-border bg-muted/20 p-4"
      data-testid="new-automation-form"
    >
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-1.5 md:col-span-2">
          <Label>
            Nom de la règle <span className="text-red-500">*</span>
          </Label>
          <Input
            data-testid="new-automation-nom"
            value={form.nom}
            onChange={(e) => set("nom", e.target.value)}
            placeholder="ex: Relance auto leads HOT sans contact 3j"
            className={
              submitAttempted && nomMissing
                ? "border-red-400 focus-visible:ring-red-300"
                : ""
            }
            aria-invalid={submitAttempted && nomMissing}
          />
          {submitAttempted && nomMissing && (
            <p className="text-[11px] font-medium text-red-600" data-testid="new-automation-nom-error">
              Le nom de la règle est obligatoire.
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label>Déclencheur</Label>
          <Select value={form.trigger} onValueChange={(v) => set("trigger", v)}>
            <SelectTrigger data-testid="new-automation-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRIGGERS.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Seuil (jours)</Label>
          <Input
            type="number"
            min={0}
            max={365}
            data-testid="new-automation-days"
            value={form.days_threshold}
            onChange={(e) => set("days_threshold", e.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <Label>Filtre statut (optionnel)</Label>
          <Select
            value={form.statut_filter || "all"}
            onValueChange={(v) => set("statut_filter", v)}
          >
            <SelectTrigger data-testid="new-automation-statut">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts actifs</SelectItem>
              {STATUTS.filter((s) => !["gagne", "perdu"].includes(s.value)).map(
                (s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Score minimum (optionnel)</Label>
          <Input
            type="number"
            min={0}
            max={4}
            data-testid="new-automation-score"
            value={form.score_min}
            onChange={(e) => set("score_min", e.target.value)}
            placeholder="ex: 3"
          />
        </div>

        <div className="space-y-1.5 md:col-span-2">
          <Label>Action</Label>
          <Select value={form.action} onValueChange={(v) => set("action", v)}>
            <SelectTrigger data-testid="new-automation-action">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACTIONS.map((a) => (
                <SelectItem key={a.value} value={a.value}>
                  {a.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {form.action === "create_rappel" && (
          <div className="space-y-1.5 md:col-span-2">
            <Label>Message du rappel</Label>
            <Input
              data-testid="new-automation-message"
              value={form.rappel_message}
              onChange={(e) => set("rappel_message", e.target.value)}
              placeholder="Relance automatique..."
            />
          </div>
        )}
      </div>

      <div className="mt-3 flex justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          data-testid="new-automation-cancel"
          className="gap-1.5"
        >
          <X className="h-3.5 w-3.5" />
          Annuler
        </Button>
        <Button
          size="sm"
          onClick={submit}
          disabled={saving}
          data-testid="new-automation-submit"
          className="gap-1.5"
        >
          <Save className="h-3.5 w-3.5" />
          Créer la règle
        </Button>
      </div>
    </div>
  );
};

export default function AutomationsPanel({ projetId = null, projetNom = "" }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Scope rules to the active project. New rules created here are tagged
      // to this project; the runner already filters leads by projet_id.
      const data = await automationsApi.list(projetId);
      setRules(data || []);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [projetId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (rule) => {
    await automationsApi.update(rule.id, { enabled: !rule.enabled });
    toast.success(rule.enabled ? "Règle désactivée" : "Règle activée");
    load();
  };

  const remove = async (id, nom) => {
    if (!window.confirm(`Supprimer la règle « ${nom} » ?`)) return;
    await automationsApi.remove(id);
    toast.success("Règle supprimée");
    load();
  };

  const run = async (id) => {
    setRunning(true);
    try {
      const res = await automationsApi.run(id);
      toast.success(
        `Règle exécutée · ${res.matched} ciblés · ${res.created} créés · ${res.skipped_existing} ignorés`
      );
    } catch (e) {
      toast.error("Erreur lors de l'exécution");
    } finally {
      setRunning(false);
    }
  };

  const runAll = async () => {
    setRunning(true);
    try {
      const res = await automationsApi.runAll();
      toast.success(`${res.rules_run} règle(s) exécutée(s)`);
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setRunning(false);
    }
  };

  return (
    <section
      className="rounded-xl border border-border/60 bg-card p-5"
      data-testid="automations-panel"
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-[hsl(var(--accent-gold))]" />
          <h2 className="font-heading text-lg font-semibold">
            Automatisations
            {projetNom && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                · {projetNom}
              </span>
            )}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={runAll}
            disabled={running || rules.length === 0}
            data-testid="automation-run-all"
            className="gap-1.5"
          >
            <Play className="h-3.5 w-3.5" />
            Tout exécuter
          </Button>
          <Button
            size="sm"
            onClick={() => setShowNew(true)}
            disabled={showNew}
            data-testid="automation-new-btn"
            className="gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" />
            Nouvelle règle
          </Button>
        </div>
      </div>

      <p className="mb-4 text-sm text-muted-foreground">
        Définissez des règles qui s'exécutent automatiquement chaque heure
        (ou manuellement à la demande) pour créer des rappels et booster
        votre suivi commercial.
      </p>

      <Separator className="mb-4" />

      <div className="space-y-3" data-testid="automations-list">
        {loading ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Chargement...
          </div>
        ) : rules.length === 0 && !showNew ? (
          <p className="rounded-lg border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
            Aucune règle d'automatisation. Créez-en une pour automatiser vos
            relances.
          </p>
        ) : (
          rules.map((r) => (
            <RuleRow
              key={r.id}
              rule={r}
              onToggle={toggle}
              onDelete={remove}
              onRun={run}
              running={running}
            />
          ))
        )}
      </div>

      {showNew && (
        <div className="mt-4">
          <NewRuleForm
            projetId={projetId}
            onCreated={() => {
              setShowNew(false);
              load();
            }}
            onCancel={() => setShowNew(false)}
          />
        </div>
      )}
    </section>
  );
}
